/**
 * AcquireOS Idle Notification Service
 *
 * Tracks user activity via localStorage timestamps and sends nudge emails
 * when one of three conditions has been idle for ≥ 4 hours:
 *
 *   Scenario 1 — No deal selected and user hasn't been active for IDLE_THRESHOLD_MS
 *   Scenario 2 — Deal selected but no module progress for IDLE_THRESHOLD_MS
 *   Scenario 3 — OM not uploaded for IDLE_THRESHOLD_MS after reaching Module 3
 *
 * Emails are sent at most once per NOTIF_COOLDOWN_MS per scenario to avoid re-flooding.
 * All timestamps are stored in localStorage so they survive page refreshes.
 */

import {
  sendIdleDashboardEmail,
  sendModuleStallEmail,
  sendMissingOMEmail,
} from './workflowNotificationService';

// ── Constants ─────────────────────────────────────────────────────────────────
const IDLE_THRESHOLD_MS   = 4 * 60 * 60 * 1000;  // 4 hours
const NOTIF_COOLDOWN_MS   = 24 * 60 * 60 * 1000; // 24 hours

// localStorage keys
const K = {
  lastActive:     'aq_last_active',        // any navigation / user action
  lastModuleAt:   'aq_last_module_at',     // last time user advanced a module
  omMissingSince: 'aq_om_missing_since',   // first time OM-missing was detected
  trackedDealId:  'aq_tracked_deal_id',    // deal currently being tracked
  sentIdleAt:     'aq_sent_idle_at',       // when idle-dashboard email was last sent
  sentStallAt:    'aq_sent_stall_at',      // when stall email was last sent
  sentOMAt:       'aq_sent_om_at',         // when OM-missing email was last sent
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getTs  = (key) => parseInt(localStorage.getItem(key) || '0') || 0;
const setTs  = (key, ts = Date.now()) => localStorage.setItem(key, String(ts));
const clearK = (key) => localStorage.removeItem(key);

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Call on any user interaction (module navigation, page load).
 * Keeps the "last active" timestamp fresh so we know the user was here.
 */
export function recordActivity() {
  setTs(K.lastActive);
  try {
    const savedSession = localStorage.getItem('acquireos_session');
    if (savedSession) {
      const activeUser = JSON.parse(savedSession)?.user;
      if (activeUser?.email && activeUser.role !== 'admin') {
        fetch('/api/admin/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail: activeUser.email,
            component: 'Client Portal',
            message: 'User session active / page navigation action',
            severity: 'Low',
            status: 'Resolved'
          })
        }).catch(() => {});
      }
    }
  } catch (e) {}
}

/**
 * Call when a deal is initialized / selected.
 * Resets per-deal tracking if the dealId changed.
 */
export function recordDealStart(dealId) {
  if (!dealId) return;
  const tracked = localStorage.getItem(K.trackedDealId);
  if (tracked !== dealId) {
    // New deal — reset per-deal timestamps
    localStorage.setItem(K.trackedDealId, dealId);
    setTs(K.lastModuleAt);
    clearK(K.omMissingSince);
    clearK(K.sentStallAt);
    clearK(K.sentOMAt);
  }
}

/**
 * Call each time the user navigates to or completes a module.
 * Updates the "last module activity" timestamp.
 */
export function recordModuleActivity(moduleNumber) {
  setTs(K.lastModuleAt);
  try {
    const savedSession = localStorage.getItem('acquireos_session');
    if (savedSession) {
      const activeUser = JSON.parse(savedSession)?.user;
      if (activeUser?.email && activeUser.role !== 'admin') {
        fetch('/api/admin/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail: activeUser.email,
            component: moduleNumber ? `Module ${moduleNumber}` : 'Underwriting Checklist',
            message: `User advanced pipeline progress to ${moduleNumber ? 'Module ' + moduleNumber : 'checklist'}`,
            severity: 'Low',
            status: 'Resolved'
          })
        }).catch(() => {});
      }
    }
  } catch (e) {}
}

/**
 * Call from Module 3 whenever the OM upload status is known.
 * Starts the 4-hour countdown when OM is missing; clears it when uploaded.
 *
 * @param {boolean} isUploaded  true if the OM file is present
 */
export function recordOMStatus(isUploaded) {
  if (isUploaded) {
    clearK(K.omMissingSince);
    clearK(K.sentOMAt); // reset so a future OM-missing scenario re-notifies
  } else {
    // Only set the "missing since" timestamp once (don't reset on subsequent renders)
    if (!getTs(K.omMissingSince)) {
      setTs(K.omMissingSince);
    }
  }
}

/**
 * Check all three idle scenarios and send nudge emails if thresholds are exceeded.
 * Safe to call on every mount and focus event — internal cooldowns prevent re-flooding.
 *
 * @param {object} dealState  Zustand dealState
 * @param {string} userEmail  Logged-in user's email
 */
export async function checkIdleNotifications(dealState, userEmail) {
  if (!userEmail) return;
  const now = Date.now();

  // ── Scenario 1: No deal selected, user idle for 4h ──────────────────────────
  if (!dealState?.dealId) {
    const lastActive = getTs(K.lastActive);
    const lastSent   = getTs(K.sentIdleAt);
    const idleLong   = lastActive && (now - lastActive >= IDLE_THRESHOLD_MS);
    const notSpammed = now - lastSent >= NOTIF_COOLDOWN_MS;

    if (idleLong && notSpammed) {
      console.info('[IdleNotif] Scenario 1 — no deal selected, idle 4h. Sending email.');
      const result = await sendIdleDashboardEmail(userEmail);
      if (result.success !== false) setTs(K.sentIdleAt, now);
    }
  } else {
    // User has a deal — reset the idle-dashboard sent flag so it can fire again if they drop off
    clearK(K.sentIdleAt);
  }

  // ── Scenario 2: Deal selected but no module progress for 4h ─────────────────
  if (dealState?.dealId) {
    const lastModule = getTs(K.lastModuleAt);
    const lastSent   = getTs(K.sentStallAt);
    const stalledLong = lastModule && (now - lastModule >= IDLE_THRESHOLD_MS);
    const notSpammed  = now - lastSent >= NOTIF_COOLDOWN_MS;

    if (stalledLong && notSpammed) {
      console.info('[IdleNotif] Scenario 2 — module stalled for 4h. Sending email.');
      const result = await sendModuleStallEmail(userEmail, dealState);
      if (result.success !== false) setTs(K.sentStallAt, now);
    }
  }

  // ── Scenario 3: OM missing for 4h ───────────────────────────────────────────
  if (dealState?.dealId && !dealState?.financialDocuments?.omUploaded) {
    const omMissingSince = getTs(K.omMissingSince);
    const lastSent       = getTs(K.sentOMAt);
    const missingLong    = omMissingSince && (now - omMissingSince >= IDLE_THRESHOLD_MS);
    const notSpammed     = now - lastSent >= NOTIF_COOLDOWN_MS;

    if (missingLong && notSpammed) {
      console.info('[IdleNotif] Scenario 3 — OM missing for 4h. Sending email.');
      const result = await sendMissingOMEmail(userEmail, dealState);
      if (result.success !== false) setTs(K.sentOMAt, now);
    }
  }
}
